import React, { useState, useEffect } from 'react';
import useInView from '../../hooks/useInView';
import './Solution.css';

const LANDLORD_ITEMS = [
  'Decades of Experience',
  'Deposit Tracking Software',
  'Tenant Screening Data',
  'Automated Rent Collection',
  'Legal Teams',
];

const RENTER_ITEMS = [
  'A lease they barely understand',
  'A deposit they\'ll probably lose',
];

// Absolute ms delays for each state change
const TIMINGS = [
  { type: 'landlord', count: 1, delay: 600 },
  { type: 'renter',   count: 1, delay: 1000 },
  { type: 'landlord', count: 2, delay: 1700 },
  { type: 'renter',   count: 2, delay: 2000 },
  { type: 'landlord', count: 3, delay: 2400 },
  { type: 'landlord', count: 4, delay: 3100 },
  { type: 'landlord', count: 5, delay: 3800 },
  { type: 'rentwise',           delay: 4800 },
];

const SolutionPill = ({ text, visible, variant }) => (
  <div
    className={`solution-pill solution-pill--${variant}${visible ? ' solution-pill--visible' : ''}`}
  >
    {text}
  </div>
);

const Solution = () => {
  const { ref, inView } = useInView({ threshold: 0.25 });
  const { ref: sceneRef, inView: sceneInView } = useInView({ threshold: 0.4 });
  const [landlordCount, setLandlordCount] = useState(0);
  const [renterCount, setRenterCount]     = useState(0);
  const [rentwiseDropped, setRentwiseDropped] = useState(false);

  useEffect(() => {
    if (!sceneInView) return;
    const timers = TIMINGS.map(({ type, count, delay }) =>
      setTimeout(() => {
        if (type === 'landlord') setLandlordCount(count);
        else if (type === 'renter') setRenterCount(count);
        else setRentwiseDropped(true);
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [sceneInView]);

  const tilt = rentwiseDropped ? 0 : -Math.min(landlordCount * 3, 15);

  return (
    <section
      ref={ref}
      className={`solution${inView ? ' solution--visible' : ''}`}
    >
      <div className="solution__eyebrow-bar">
        <span className="solution__eyebrow">The Solution</span>
      </div>
      <div className="solution__container">
        {/* Header */}
        <div className="solution__header">
          <h2 className="solution__headline">
            Leveling the{' '}
            <span className="solution__headline--accent">playing field.</span>
          </h2>
        </div>

        {/* Body: scale + copy */}
        <div className="solution__body">
          {/* Scale visualization */}
          <div className="solution__scale-wrap">
            <div className="solution__scene" ref={sceneRef}>
              {/* Tilting assembly */}
              <div
                className={`solution__beam-assembly${rentwiseDropped ? ' solution__beam-assembly--rebalancing' : ''}`}
                style={{ transform: `rotate(${tilt}deg)` }}
              >
                {/* Beam bar */}
                <div className="solution__beam">
                  <span className="solution__beam-label">Landlords</span>
                  <span className="solution__beam-label">Renters</span>
                </div>

                {/* Landlord items — stacked above left end */}
                <div className="solution__items solution__items--landlord">
                  {LANDLORD_ITEMS.map((item, i) => (
                    <SolutionPill
                      key={item}
                      text={item}
                      visible={i < landlordCount}
                      variant="landlord"
                    />
                  ))}
                </div>

                {/* Renter items — stacked above right end */}
                <div className="solution__items solution__items--renter">
                  {RENTER_ITEMS.map((item, i) => (
                    <SolutionPill
                      key={item}
                      text={item}
                      visible={i < renterCount}
                      variant="renter"
                    />
                  ))}
                  <SolutionPill
                    text="✦ RentWise"
                    visible={rentwiseDropped}
                    variant="rentwise"
                  />
                </div>
              </div>

              {/* House fulcrum */}
              <div className="solution__house">
                <svg width="72" height="72" viewBox="0 0 24 24" fill="var(--color-text-near-black)" aria-hidden="true">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className="solution__copy">
            <p className="solution__body-text">
              For too long, landlords have had every tool imaginable to protect
              their investment — while renters have had{' '}
              <strong>none</strong>.
            </p>
            <p className="solution__body-text">
              <span className="solution__brand">RentWise</span> is built to
              change that. Designed to work with or without your landlord,
              RentWise makes it seamless to document everything in one place —
              whether you choose to share it with your landlord or simply keep
              it for yourself.
            </p>
            <p className="solution__body-text">
              For the first time, renters have a voice — and a real chance at
              protecting their biggest monthly expense.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;
